import React from "react"
import _ from "lodash"

import { Project, AttributeTypeGroup } from "../../../../api/types"
import { GetApi } from "../../../../api/api"
import Button from "../../../../components/Button"
import Table, {
  AddButton,
  ActionPopover,
  ActionPopoverButton,
} from "../../../../components/Table"
import AppContext from "../../../../components/AppContext"
import { LockBadge } from "../../../../components/Badge"

import EditModal from "./components/EditModal"
import { disableIfIntroducedInPreviousVersionOrOverride } from "../../../../components/disableInputPolicy"

interface PropertySetsProps {
  featureCatalog: Project
  propertySets: AttributeTypeGroup[]
  refresh: () => void
  disabled: boolean
  setDisabled: (value: boolean) => void
}

interface PropertySetsState {
  showEditModal: boolean
  isNewPropSet?: boolean
  editPropSet?: AttributeTypeGroup
  disabled: boolean
}

class PropertySets extends React.Component<
  PropertySetsProps,
  PropertySetsState
> {
  public static contextType = AppContext

  constructor(props: PropertySetsProps) {
    super(props)
    this.state = {
      showEditModal: false,
      disabled: props.disabled,
    }
  }

  public componentDidUpdate(prevProps: PropertySetsProps) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        disabled: this.props.disabled,
      })
    }
  }

  public render() {
    const rowData = this.props.propertySets.map((ps) => {
      return {
        icon: (
          <span className="d-flex justify-content-center">
            <i className="modus-icons">orders</i>
          </span>
        ),
        name: ps.name,
        action: (
          <this.EditButtons
            atg={ps}
            currentVersion={this.props.featureCatalog.version}
            disabled={this.state.disabled}
          />
        ),
      }
    })

    const columnData = [
      {
        Header: "",
        accessor: "icon",
        width: 70,
        sortable: false,
      },
      {
        Header: "Property Sets",
        accessor: "name",
      },
      {
        Header: (
          <AddButton
            onClick={this.createAttributeTypeGroup}
            disabled={this.state.disabled}
          />
        ),
        accessor: "action",
        width: 70,
        sortable: false,
      },
    ]

    return (
      <React.Fragment>
        {this.state.showEditModal && (
          <EditModal
            atgId={this.state.editPropSet!.id}
            fccId={this.props.featureCatalog.customizationId!}
            currentVersion={this.props.featureCatalog.version!}
            maximumStreamVersion={this.props.featureCatalog.streamVersion!}
            toggle={this.toggleEditModal}
            isOpen={this.state.showEditModal}
            headerText={
              this.state.isNewPropSet ? "New Property Set" : "Edit Property Set"
            }
            toggleText={this.state.isNewPropSet ? "Create" : "Done"}
            onCancel={
              this.state.isNewPropSet
                ? () => this.deleteAttributeTypeGroup(this.state.editPropSet!)
                : undefined
            }
            size="xl"
          />
        )}
        {rowData.length > 0 ? (
          <Table data={rowData} columns={columnData} />
        ) : (
          <Button
            variant="primary"
            onClick={this.createAttributeTypeGroup}
            disabled={this.state.disabled}
          >
            New property set
          </Button>
        )}
      </React.Fragment>
    )
  }

  private EditButtons = (props: {
    atg: AttributeTypeGroup
    currentVersion?: number
    disabled: boolean
  }) => {
    const editPropertySet = () => this.toggleEditModal(props.atg)
    const deleteAttributeTypeGroup = () =>
      this.deleteAttributeTypeGroup(props.atg)

    return (
      <ActionPopover>
        <ActionPopoverButton
          onClick={editPropertySet}
          disabled={props.disabled}
        >
          <span>Edit</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton
          variant="danger"
          onClick={deleteAttributeTypeGroup}
          disabled={disableIfIntroducedInPreviousVersionOrOverride(
            props.atg,
            props.currentVersion,
            props.disabled
          )}
        >
          <span>Delete</span>
          {props.atg.introducedInVersion !== props.currentVersion && (
            <LockBadge className="ml-3" variant="danger" />
          )}
        </ActionPopoverButton>
      </ActionPopover>
    )
  }

  private createAttributeTypeGroup = async () => {
    // this.setDisabled(true); // Issue with the panel refresh, so turn off for now
    try {
      const api = await GetApi()
      const attributeTypeGroup = await api.createAttributeTypeGroup(
        this.props.featureCatalog.customizationId!,
        {
          name: "NewPropertySet",
          description: "",
        }
      )
      this.toggleEditModal(attributeTypeGroup, true)
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private deleteAttributeTypeGroup = async (atg: AttributeTypeGroup) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      if (this.state.showEditModal === true) {
        this.toggleEditModal(atg, true)
        await api.deleteAttributeTypeGroup(
          this.props.featureCatalog.customizationId!,
          atg.id
        )
        this.props.refresh()
      } else {
        if (
          window.confirm("Are you sure you want to delete this property set?")
        ) {
          await api.deleteAttributeTypeGroup(
            this.props.featureCatalog.customizationId!,
            atg.id
          )
          this.props.refresh()
          this.context.useToasts.addToast(
            `Successfully deleted the property set '${atg.name}'.`,
            "success",
            true
          )
        }
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private toggleEditModal = (
    atg?: AttributeTypeGroup,
    isNew: boolean = false
  ) => {
    this.setState(
      (prevState) => ({
        isNewPropSet: isNew,
        editPropSet: atg,
        showEditModal: !prevState.showEditModal,
      }),
      () => {
        if (this.state.showEditModal === false && !this.state.isNewPropSet) {
          this.props.refresh()
        }
      }
    )
  }

  private setDisabled = (value: boolean) => {
    this.props.setDisabled(value)
    this.setState({
      disabled: value,
    })
  }
}

export default PropertySets
